import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import { ErrorBoundary } from "@/app/ErrorBoundary";
import { RequireAuth } from "@/app/RequireAuth";
import { DemoSwitcher } from "@/features/dev/DemoSwitcher";
import { OnboardingPage } from "@/features/onboarding";
import { ThemeSwitcher, useApplyTheme } from "@/features/theme";
import { VenueOnboardingPage } from "@/features/venue-onboarding";
import { AuthCallbackPage } from "@/pages/AuthCallbackPage";
import { ForgotPasswordPage } from "@/pages/ForgotPasswordPage";
import { LandingPage } from "@/pages/LandingPage";
import { LoginPage } from "@/pages/LoginPage";
import { OpsPage } from "@/pages/OpsPage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";
import { SignupPage } from "@/pages/SignupPage";
import { VenueHomePage } from "@/pages/VenueHomePage";
import { VerifyEmailPage } from "@/pages/VerifyEmailPage";
import { WorkerHomePage } from "@/pages/WorkerHomePage";

// `refetchOnWindowFocus: true` so a user returning to the tab gets fresh
// data — our explicit invalidations only reach the actor who triggered the
// mutation, so the *other* actors (worker on phone, ops on desktop) need a
// passive refresh path. The 2-minute staleTime keeps the cost low: focus
// events on still-fresh data are no-ops.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: true,
      staleTime: 2 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    },
    mutations: {
      retry: 0,
    },
  },
});

export function App() {
  useApplyTheme();
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
            <Route
              path="/worker/onboarding"
              element={
                <RequireAuth role="WORKER">
                  <OnboardingPage />
                </RequireAuth>
              }
            />
            <Route
              path="/worker/*"
              element={
                <RequireAuth role="WORKER">
                  <WorkerHomePage />
                </RequireAuth>
              }
            />
            <Route
              path="/venue/onboarding"
              element={
                <RequireAuth role="VENUE">
                  <VenueOnboardingPage />
                </RequireAuth>
              }
            />
            <Route
              path="/venue/*"
              element={
                <RequireAuth role="VENUE">
                  <VenueHomePage />
                </RequireAuth>
              }
            />
            <Route path="/ops/*" element={<OpsPage />} />
          </Routes>
          <ThemeSwitcher />
          <DemoSwitcher />
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
